import type { JSX } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUrlState } from '@/hooks/useUrlState';
import { ApiError } from '../api/types';
import { useConnectTracking, useEmailStatus } from '../hooks/email';
import { Note, Segmented } from '../workspace/ui';
import { AudienceTab } from './AudienceTab';
import { CampaignsTab } from './CampaignsTab';
import type { EmailStatus } from './types';

const TABS = [
  { value: 'audience', label: 'Audience' },
  { value: 'campaigns', label: 'Campaigns' },
] as const;
type Tab = (typeof TABS)[number]['value'];

/**
 * Email outreach: the creators we have an address for, split into segments,
 * sent to Instantly (or exported), with results read back per segment.
 */
export function EmailScreen(): JSX.Element {
  const [url, setUrl] = useUrlState({ tab: 'audience' as string });
  const tab: Tab = url.tab === 'campaigns' ? 'campaigns' : 'audience';
  const { data: status, error } = useEmailStatus();
  // An older Discovery service has no email endpoints at all.
  const unsupported = error instanceof ApiError && error.status === 404;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-bold tracking-[-0.03em]">Emails</h1>
          <p className="mt-1 max-w-[75ch] text-sm text-muted-foreground">
            Creators you have an email address for. Pick a segment, send it to an Instantly campaign or export it, and
            see what each segment did.
          </p>
        </div>
        <Segmented label="View" value={tab} onChange={(t) => setUrl({ tab: t })} options={TABS} />
      </header>

      {unsupported ? (
        <Note tone="warning" role="status">
          This page needs a Discovery service update that hasn't finished deploying.
        </Note>
      ) : (
        <>
          {status && <ConnectionNote status={status} />}
          {tab === 'audience' ? <AudienceTab status={status} /> : <CampaignsTab />}
        </>
      )}
    </div>
  );
}

/** Says what is and isn't wired up — only when something needs doing. */
function ConnectionNote({ status }: { status: EmailStatus }): JSX.Element | null {
  const connect = useConnectTracking();
  if (!status.instantly.connected) {
    return (
      <Note tone="info" role="status">
        Instantly isn't connected yet, so you can build segments and export CSVs but not send from here. To connect it,
        add an Instantly API key to the Discovery service as <code className="font-mono text-xs">INSTANTLY_API_KEY</code>.
      </Note>
    );
  }
  const tracking = status.tracking;
  if (!tracking || tracking.ready) return null;
  // Not registered is a one-click fix; a missing setting is not.
  const fixable = tracking.url !== null && !/not set/i.test(tracking.problem ?? '');
  return (
    <Note
      tone="warning"
      role="status"
      action={
        fixable ? (
          <Button
            size="sm"
            variant="outline"
            disabled={connect.isPending}
            onClick={() =>
              connect.mutate(undefined, {
                onSuccess: (r) => (r.ready ? toast.success('Live tracking is on') : toast.error(r.problem ?? "Couldn't turn tracking on")),
                onError: (e) => toast.error((e as Error).message),
              })
            }
          >
            {connect.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Turn on live tracking
          </Button>
        ) : undefined
      }
    >
      Live tracking is off: {tracking.problem ?? 'Instantly has nowhere to send events.'} Results still update about every
      30 minutes, but opens and replies won't appear as they happen.
    </Note>
  );
}
